import * as React from 'react';

import { DescriptionPage_ProductVariant as ProductVariant } from '__generated__/graphql-types';

import ReviewsBackButton from 'bundles/reviews/components/ReviewsBackButton';
import ReviewsPageLayout from 'bundles/reviews/components/ReviewsPageLayout';
import ReviewsProductCard from 'bundles/reviews/components/ReviewsProductCard';
import ReviewsRatingCard from 'bundles/reviews/components/ReviewsRatingCard';
import ReviewsTable from 'bundles/reviews/components/ReviewsTable';
import TopReviewsSection from 'bundles/reviews/components/TopReviewsSection';
import type { SpecializationReviewsPageData } from 'bundles/reviews/hooks/useSpecializationReviewsRouteData';
import ConsumerSpecializationEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerSpecializationEnrollButtonNotConfigurable';
import { useSpecializationCourseFilter } from 'bundles/unified-description-page-common/hooks/useSpecializationCourseFilter';
import { useSpecializationReviewsPageData } from 'bundles/unified-description-page-common/hooks/useSpecializationReviewsData';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import { getMockReviewsPageTopReviews } from 'bundles/unified-description-page-common/utils/getMockSpecializationReviews';
import {
  getConsumerRouteForSpecializationProducts,
  getEnterpriseRouteForSpecializationProducts,
} from 'bundles/unified-description-page-common/utils/getRouteForSpecializationProducts';

import _t from 'i18n!nls/reviews';

const PROFESSIONAL_CERTIFICATE_VARIANTS: ProductVariant[] = [
  ProductVariant.ProfessionalCertificate,
  ProductVariant.ExternalCertificate,
  ProductVariant.GoogleCertificate,
];

type Props = {
  pageData: SpecializationReviewsPageData;
  productVariant: ProductVariant;
  slug: string;
  programSlug?: string;
  orgSlug?: string;
};

const SpecializationReviewsPageContent = ({ pageData, productVariant, slug, programSlug, orgSlug }: Props) => {
  const isProfessionalCertificate = PROFESSIONAL_CERTIFICATE_VARIANTS.includes(productVariant);
  const { courseFilterOptions, selectedCourseSlug, setSelectedCourseSlug, selectedCourseId } =
    useSpecializationCourseFilter(pageData.courses);
  const { ratingCountsByStar } = useSpecializationReviewsPageData(pageData);
  const topReviews = getMockReviewsPageTopReviews(pageData.courses, selectedCourseId);

  const backHref =
    orgSlug || programSlug
      ? getEnterpriseRouteForSpecializationProducts(productVariant, slug, programSlug, undefined, orgSlug)
      : getConsumerRouteForSpecializationProducts(productVariant, slug);

  const partner = pageData.partners?.[0];
  // Only the consumer fragment includes cardImageUrl -- the `in` check narrows the pageData union.
  const imageUrl = 'cardImageUrl' in pageData ? pageData.cardImageUrl : null;
  // No org/program scope means the route data hook ran the consumer query, so pageData is
  // guaranteed to be consumer-shaped here. Enterprise/org-scoped reviews pages show no enrollment
  // CTA -- EnterpriseSpecializationEnrollButtonNotConfigurable relies on several context hooks
  // (program/org membership, etc.) that assume it's mounted inside the full XDP provider tree.
  const enrollmentCta =
    orgSlug || programSlug ? null : (
      <ConsumerSpecializationEnrollButtonNotConfigurable
        pageData={pageData as ConsumerSpecializationPageFieldsFragment}
      />
    );

  return (
    <ReviewsPageLayout
      backButton={<ReviewsBackButton courseName={pageData.name} href={backHref} />}
      productCard={
        <ReviewsProductCard
          courseId={pageData.id}
          courseName={pageData.name}
          description={pageData.description}
          difficultyLevel={pageData.difficultyLevel}
          enrollmentCta={enrollmentCta}
          imageUrl={imageUrl}
          marketingProductType={isProfessionalCertificate ? 'PROFESSIONAL_CERTIFICATE' : 'SPECIALIZATION'}
          partnerLogoUrl={partner?.logo}
          partnerName={partner?.name}
          programSlug={programSlug}
          skillTags={(pageData.skillTags ?? []).map((tag) => tag.name)}
        />
      }
      ratingCard={
        <ReviewsRatingCard
          averageFiveStarRating={pageData.ratings?.averageFiveStarRating}
          ratingCountsByStar={ratingCountsByStar}
          numberOfCourses={pageData.courses?.length}
        />
      }
    >
      <TopReviewsSection
        title={_t('Top reviews across #{name}', { name: pageData.name })}
        reviews={topReviews.map((review) => ({ key: review.id, ...review }))}
      />
      {/* ProductReviewsV1Resource.byProduct's productId is a union field -- courses must be
          passed as a "COURSE~<id>" stringkey, not the bare course id. */}
      {selectedCourseId ? (
        <ReviewsTable
          courseId={`COURSE~${selectedCourseId}`}
          courseFilterOptions={courseFilterOptions}
          selectedCourseSlug={selectedCourseSlug}
          onCourseChange={setSelectedCourseSlug}
          specializationName={pageData.name}
        />
      ) : null}
    </ReviewsPageLayout>
  );
};

export default SpecializationReviewsPageContent;
